import { useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertCircle, Plus, Trash2, Package, Search, ChevronDown } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';

interface Product {
  _id: string;
  displayName?: string;
  drugName?: string;
  concentration?: string;
  format?: string;
  size?: string;
  isActive?: boolean;
  /** Physician-facing price resolved from the pricing tree for this order's owner. */
  finalPrice?: number;
}

const fmtPrice = (n?: number) =>
  typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

const productLabel = (p: Product) =>
  p.displayName ||
  [p.drugName, p.concentration, p.format, p.size].filter(Boolean).join(' ') ||
  p._id;

// Cap rendered rows so a broad query can't paint the whole (up to 500-item)
// catalog at once. Anything beyond this is hidden behind a "refine search" hint.
const MAX_VISIBLE = 50;

/**
 * Type-to-filter product picker. Replaces a native <select> that listed the
 * entire active catalog — filters the already-loaded products client-side,
 * click or Enter to pick, Escape / outside-click to close.
 */
function ProductCombobox({
  products,
  value,
  onChange,
  disabled,
  loading,
}: {
  products: Product[];
  value: string;
  onChange: (productId: string) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = products.find(p => p._id === value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? products.filter(p => productLabel(p).toLowerCase().includes(q))
      : products;
    return list;
  }, [products, query]);

  const visible = matches.slice(0, MAX_VISIBLE);
  const hiddenCount = matches.length - visible.length;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the highlighted row in range as the filter narrows.
  useEffect(() => { setHighlight(0); }, [query]);

  const pick = (p: Product) => {
    onChange(p._id);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, visible.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (visible[highlight]) pick(visible[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="relative" ref={rootRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={open ? query : (selected ? productLabel(selected) : '')}
          onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onKeyDown={onKeyDown}
          disabled={disabled}
          placeholder={loading ? 'Loading products…' : (selected ? productLabel(selected) : 'Search products…')}
          className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
        />
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      </div>

      {open && !loading && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
          {visible.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No matching products</div>
          ) : (
            <>
              {visible.map((p, idx) => (
                <button
                  key={p._id}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); pick(p); }}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-sm ${
                    idx === highlight ? 'bg-blue-50 text-blue-900' : 'text-gray-700'
                  } ${p._id === value ? 'font-medium' : ''}`}
                >
                  <span className="truncate text-left">{productLabel(p)}</span>
                  {typeof p.finalPrice === 'number' && (
                    <span className="flex-shrink-0 tabular-nums text-gray-500">{fmtPrice(p.finalPrice)}</span>
                  )}
                </button>
              ))}
              {hiddenCount > 0 && (
                <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                  {hiddenCount} more — keep typing to narrow
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface NewLine {
  productId: string;
  quantity: number;
}

interface Props {
  orderId: string;
  orderNumber?: string;
  orderType?: 'order' | 'bulk_order';
  /** The order owner's physician id — scopes the product picker to that physician's catalog/pricing. */
  physicianId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddItemsModal({ orderId, orderNumber, orderType = 'order', physicianId, onClose, onSuccess }: Props) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState<NewLine[]>([{ productId: '', quantity: 1 }]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!physicianId) {
      setError('Order has no physician on file, so the available product list can’t be resolved.');
      return;
    }
    setLoading(true);
    setError(null);
    // Scope the picker to exactly what this order's physician can order, with the
    // price resolved from the pricing tree — the same source the add-items
    // backend validates against (so we never offer a product it would reject).
    apiClient.adminViewPhysicianPricing(physicianId)
      .then(res => {
        if (cancelled) return;
        if (!res.success) { setError(res.error || 'Failed to load products'); return; }
        const list: any[] = (res.data as any)?.products || [];
        setProducts(
          list
            .filter(p => p.isActive !== false)
            .map(p => ({
              _id: p._id,
              displayName: p.displayName,
              drugName: p.drugName,
              concentration: p.concentration,
              format: p.format,
              size: p.size,
              isActive: p.isActive,
              finalPrice: p.calculatedPrice?.finalPrice,
            }))
        );
      })
      .catch(() => { if (!cancelled) setError('Failed to load products'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [physicianId]);

  const addLine = () => setLines([...lines, { productId: '', quantity: 1 }]);
  const removeLine = (i: number) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i: number, patch: Partial<NewLine>) =>
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const validLines = lines.filter(l => l.productId && l.quantity > 0);
  const dirty = validLines.length > 0 && reason.trim().length >= 5;

  const handleSubmit = async () => {
    if (!dirty) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = orderType === 'bulk_order'
        ? await apiClient.addBulkOrderItems(orderId, { adds: validLines, reason: reason.trim() })
        : await apiClient.addOrderItems(orderId, { adds: validLines, reason: reason.trim() });

      if (res.success) {
        const data = res.data as any;
        const submitErr = data?.childSubmitError;
        const childNumber = data?.child?.orderNumber || data?.child?.bulkOrderNumber;
        const pharmacyIds: string[] = Array.isArray(data?.childPharmacyOrderIds)
          ? data.childPharmacyOrderIds
          : data?.childPharmacyOrderId ? [data.childPharmacyOrderId] : [];

        if (submitErr) {
          toast.success(`Child ${orderType === 'bulk_order' ? 'bulk' : 'order'} created; pharmacy submit failed: ${submitErr}`);
        } else if (pharmacyIds.length > 0) {
          toast.success(`Child ${orderType === 'bulk_order' ? 'bulk' : 'order'} ${childNumber || ''} submitted (${pharmacyIds.length} pharmacy id${pharmacyIds.length === 1 ? '' : 's'}).`);
        } else if (childNumber) {
          toast.success(`Child ${orderType === 'bulk_order' ? 'bulk' : 'order'} ${childNumber} created.`);
        } else {
          toast.success(res.message || 'Items added.');
        }
        onSuccess();
        onClose();
      } else {
        setError(res.error || 'Failed to add items.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to add items.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]"
      onClick={onClose}
    >
      <div
        className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Add items {orderNumber ? `to ${orderNumber}` : ''}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 text-sm text-blue-900">
          A delta invoice is created for the additions only (no extra shipping). If the order is at the pharmacy, a sub-order is submitted automatically.
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4 text-sm text-amber-900">
            This physician has no products in their assigned catalog, so there’s nothing to add.
          </div>
        )}

        <div className="space-y-2 mb-4">
          {lines.map((line, i) => (
            <div key={i} className="flex gap-2 items-start">
              <div className="flex-1 min-w-0">
                <ProductCombobox
                  products={products}
                  value={line.productId}
                  onChange={pid => updateLine(i, { productId: pid })}
                  disabled={submitting}
                  loading={loading}
                />
              </div>
              <input
                type="number"
                min={1}
                value={line.quantity}
                onChange={e => updateLine(i, { quantity: parseInt(e.target.value) || 1 })}
                disabled={submitting}
                className="w-24 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => removeLine(i)}
                disabled={submitting || lines.length === 1}
                className="p-2 text-gray-500 hover:text-red-600 disabled:opacity-30"
                title="Remove this row"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addLine}
          disabled={submitting}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 rounded-md mb-4"
        >
          <Plus className="h-4 w-4" /> Add another item
        </button>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-red-600">*</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={2}
            minLength={5}
            maxLength={500}
            placeholder="e.g., physician requested additional 2 vials"
            disabled={submitting}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!dirty || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Adding…' : `Add ${validLines.length} item${validLines.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
